import { useEffect, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, Text, View, useWindowDimensions, Image } from 'react-native';
import { Download, FileText, FolderOpen, Image as ImageIcon, Trash2, Upload } from 'lucide-react-native';

import { Button, Card, EmptyState, SkeletonRows, StatTile } from '@/components/ui';
import { listarArchivosDoctor, subirArchivoCaso, eliminarArchivoCaso, type ArchivoCaso } from '@/services/firebase/archivos';
import { useAuth, selectIsAdmin } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';

export function ArchivosScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const perfil = useAuth((s) => s.perfil);
  const isAdmin = useAuth(selectIsAdmin);

  const [archivos, setArchivos] = useState<ArchivoCaso[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recargar = async () => {
    if (!perfil) return;
    setLoading(true);
    try {
      const lista = await listarArchivosDoctor(perfil.uid);
      setArchivos(lista);
    } catch (e: any) {
      setError(e?.message ?? 'No se pudieron listar los archivos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    recargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfil?.uid]);

  const onUpload = async () => {
    if (Platform.OS !== 'web') {
      setError('La subida desde móvil se conectará a la galería en Fase 3 (image-picker).');
      return;
    }
    if (!perfil) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf,video/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploading(true);
      setProgress(0);
      setError(null);
      try {
        await subirArchivoCaso({
          doctorUid: perfil.uid,
          file,
          filename: file.name,
          onProgress: setProgress,
        });
        await recargar();
      } catch (e: any) {
        setError(e?.message ?? 'No se pudo subir el archivo.');
      } finally {
        setUploading(false);
        setProgress(0);
      }
    };
    input.click();
  };

  const onDelete = async (a: ArchivoCaso) => {
    try {
      await eliminarArchivoCaso(a.fullPath);
      await recargar();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo eliminar.');
    }
  };

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 20, paddingBottom: 80 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <StatTile
          label="Archivos cargados"
          value={String(archivos.length)}
          accent="magenta"
          icon={<FolderOpen size={20} color={colors.magenta[700]} />}
        />
        <StatTile
          label="Capacidad usada"
          value="—"
          accent="neutral"
          hint="Próximamente"
        />
      </View>

      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <View>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 20, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Mis archivos clínicos
            </Text>
            <Text style={{ color: colors.ink[500], fontSize: 13, marginTop: 2 }}>
              Imagen, PDF o video hasta 25 MB. Solo vos y la administradora ven tu carpeta.
            </Text>
          </View>
          <Button
            label={uploading ? `Subiendo… ${progress.toFixed(0)}%` : 'Subir archivo'}
            iconLeft={<Upload size={16} color={colors.white} />}
            onPress={onUpload}
            loading={uploading}
          />
        </View>
      </Card>

      {error ? (
        <View style={{ padding: 14, borderRadius: 14, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}

      {loading ? (
        <SkeletonRows rows={3} />
      ) : archivos.length === 0 ? (
        <Card>
          <EmptyState
            illustration="note"
            title="Aún no subiste archivos"
            message="Cargá radiografías, análisis y fotos de tus casos. Quedan privados a tu carpeta."
            actionLabel="Subir el primero"
            onAction={onUpload}
          />
        </Card>
      ) : (
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 14,
          }}
        >
          {archivos.map((a) => (
            <ArchivoCard key={a.fullPath} archivo={a} canDelete={isAdmin} onDelete={() => onDelete(a)} />
          ))}
        </View>
      )}
    </ScrollView>
  );
}

function ArchivoCard({ archivo, canDelete, onDelete }: { archivo: ArchivoCaso; canDelete: boolean; onDelete: () => void }) {
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(archivo.name);
  const isPdf = /\.pdf$/i.test(archivo.name);
  return (
    <View
      style={{
        width: 240,
        borderRadius: radius['2xl'],
        backgroundColor: colors.white,
        borderWidth: 1,
        borderColor: colors.ink[100],
        overflow: 'hidden',
      }}
    >
      <View style={{ height: 140, backgroundColor: colors.crema[100], alignItems: 'center', justifyContent: 'center' }}>
        {isImage ? (
          <Image source={{ uri: archivo.url }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
        ) : isPdf ? (
          <FileText size={36} color={colors.magenta[700]} />
        ) : (
          <ImageIcon size={36} color={colors.magenta[700]} />
        )}
      </View>
      <View style={{ padding: 12, gap: 10 }}>
        <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 13 }} numberOfLines={1}>
          {archivo.name}
        </Text>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Pressable onPress={() => Linking.openURL(archivo.url)} hitSlop={6}>
            <Download size={16} color={colors.magenta[700]} />
          </Pressable>
          {canDelete ? (
            <Pressable onPress={onDelete} hitSlop={6}>
              <Trash2 size={16} color={colors.danger[600]} />
            </Pressable>
          ) : null}
        </View>
      </View>
    </View>
  );
}
